import { Component, OnInit, OnDestroy, Input, AfterViewInit, EventEmitter, Output, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { Logger } from 'src/app/api/helper/app-error-logger';
import { BildVO } from 'src/app/api/model/model';
import { File } from '@awesome-cordova-plugins/file/ngx';
import { MyApp } from 'src/app/api/helper/app';
import { FileHelper } from 'src/app/api/helper/file-helper';
import { UiHelper } from 'src/app/api/helper/ui-helper';
import { Utils } from 'src/app/api/helper/utils';
import { AppStorage } from 'src/app/api/helper/app-storage';
import { AlertController } from '@ionic/angular';
import { FileOpener } from '@awesome-cordova-plugins/file-opener/ngx';
import { WebView } from '@awesome-cordova-plugins/ionic-webview/ngx';
import { AppConfig } from 'src/app/api/helper/app.config';
import { SystemService } from 'src/app/api/system.service';
import { ImageHelper } from 'src/app/api/helper/image-helper';

import moment from 'moment';
import { CameraHelper } from '../../helper/camera-helper';

const log = new Logger('FotosPage');

@Component({
    selector: 'app-fotos',
    templateUrl: './fotos.component.html',
    styleUrls: ['./fotos.component.scss'],
    standalone: false
})
export class FotosPage implements OnInit, OnDestroy, AfterViewInit {

    subscriptions: Subscription[] = [];

    @Input() bilder: BildVO[] = [];
    @Input() filenamePrefix = '';
    @Input() readOnly = false;

    @Output() neuesBild: EventEmitter<BildVO> = new EventEmitter();
    @Output() bildGeloescht: EventEmitter<string> = new EventEmitter();

    isVisible = false;
    kameraAktiv: boolean;

    constructor(
        private file: File,
        private alertController: AlertController,
        private fileOpener: FileOpener,
        private webView: WebView,
        private cdr: ChangeDetectorRef,
    ) { }

    async ngOnInit() {
        await this.updateBilder('ngOnInit');
    }

    async ngAfterViewInit() {

    }

    ngOnDestroy() {
    }

    async ionViewWillEnter() {
    }

    ionViewDidEnter() {
    }

    ionViewWillLeave() {
    }


    onFotoAufnehmen(e: Event) {
        this.doFotoAufnehmen();
    }

    async doFotoAufnehmen() {
        if (this.readOnly) {
            return;
        }

        this.kameraAktiv = true;

        let pictureSrc = await CameraHelper.takePicture({
            filenamePrefix: this.filenamePrefix
        });

        if (pictureSrc) {
            log?.debug('pictureSrc', pictureSrc);

            const bild: BildVO = {
                datum: Utils.nowIsoDateString(),
                bild: pictureSrc,
                guid: Utils.uuid(),
                src: null
            };

            if (!this.bilder) {
                this.bilder = [];
            }

            this.bilder.push(bild);

            await AppStorage.current.set('bild-' + bild.guid, pictureSrc, false);

            this.neuesBild.next(bild);
        }

        await this.updateBilder('doFotoAufnehmen');

        this.detectChanges();

        setTimeout(() => {
            this.kameraAktiv = false;
        }, 250);
    }

    async updateBilder(info: string = ''): Promise<void> {
        log?.debug('updateBilder: ' + this.bilder?.length + ' (' + info + ')');

        if (!this.bilder) {
            this.bilder = [];
        }

        for (const bild of this.bilder) {
            let src = bild.bild;

            if (src && src.startsWith('file:')) {
                // Bild ist als Datei gespeichert und es wird hier der Pfad zurückgegeben
                src = this.webView.convertFileSrc(src);
            } else {
                // Bild ist als Base64 hinterlegt
                if (!src.startsWith("data:image/")) {
                    src = 'data:image/jpeg;base64,' + src;
                }
            }

            bild.src = src;
        }

        this.detectChanges();
    }

    detectChanges() {
        try {
            this.cdr.detectChanges();
        } catch (err) {
            log.error(Utils.getErrorMessage(err), err);
        }
    }


    frageBildAktion(): Promise<string> {
        return new Promise(async resolve => {
            if (this.readOnly) {
                // Keine Buttons notwendig. Bild kann nur angezeigt werden
                resolve('anzeigen');
                return;
            }

            const buttons: any[] = [];

            buttons.push({
                text: 'Löschen',
                role: 'destructive',
                icon: 'trash',
                handler: () => {
                    resolve('loeschen');
                }
            });

            buttons.push({
                text: 'Anzeigen',
                icon: 'image',
                handler: () => {
                    resolve('anzeigen');
                }
            });

            buttons.push({
                text: 'Aus Galerie',
                role: 'destructive',
                icon: 'trash',
                handler: () => {
                    resolve('auswaehlen');
                }
            });

            buttons.push({
                text: 'Abbruch',
                icon: 'close',
                role: 'cancel',
                handler: () => {
                    resolve(null);
                }
            });

            const alert = await this.alertController.create({
                cssClass: 'no-text-alert no-title-alert',
                mode: 'ios',
                buttons: buttons
            });

            await alert.present();
        });
    }

    async onBildClicked(vo: BildVO) {
        const aktion = await this.frageBildAktion();

        if (aktion === 'loeschen') {
            const ok = true; //  await UiHelper.confirmJaNein('', 'Bild wirklich löschen?');

            if (ok) {
                const idx = this.bilder.findIndex(p => p.guid === vo.guid);

                if (idx > -1) {
                    const guid = vo.guid;

                    this.bilder.splice(idx, 1);

                    this.bildGeloescht.next(guid);

                    this.detectChanges();

                    await AppStorage.current.loescheBild(guid);
                }
            }
        } else if (aktion === 'anzeigen') {
            this.doBildAnzeigen(vo);
        } else if (aktion === 'auswaehlen') {
            this.doBildAuswaehlen(vo);
        }
    }

    async doBildAnzeigen(vo: BildVO) {
        if (!MyApp.isCordovaAvailable()) {
            UiHelper.showAlert('Bild anzeigen in Simulation nicht verfügbar', 'Simulation');
            return;
        }

        const filePath = await AppStorage.current.getBildSrc(vo.guid);
        await this.fileOpener.open(filePath, 'image/jpeg');
    }

    async doBildAuswaehlen(vo: BildVO) {
        if (!MyApp.isCordovaAvailable()) {
            UiHelper.showAlert('Bild auswählen in Simulation nicht verfügbar', 'Simulation');
            return;
        }

        try {
            this.kameraAktiv = true;

            let pictureFilePaths: string[] = await CameraHelper.pickPicturesFromLibrary({
                filenamePrefix: this.filenamePrefix
            });

            if (pictureFilePaths?.length) {
                MyApp.loading(true);

                for (const filePath of pictureFilePaths) {
                    log?.debug('pictureSrc: ' + filePath);

                    const bild: BildVO = {
                        datum: Utils.nowIsoDateString(),
                        bild: filePath,
                        guid: Utils.uuid(),
                        src: null
                    };

                    if (!this.bilder) {
                        this.bilder = [];
                    }

                    this.bilder.push(bild);

                    await AppStorage.current.set('bild-' + bild.guid, filePath, false);

                    this.neuesBild.next(bild);
                }
            }

            await this.updateBilder('doBildAuswaehlen');

            this.detectChanges();

            setTimeout(() => {
                this.kameraAktiv = false;
            }, 250);
        } catch (err) {
            log.error('Fehler beim Auswählen von Bildern: ' + Utils.getErrorMessage(err), err);
        } finally {
            MyApp.loading(false);
        }
    }
}
